import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { trigger, style, animate, transition } from '@angular/animations'
import { Subscription } from 'rxjs'
import { IconComponent } from '../icon/icon.component'
import { Toast, ToastService } from 'src/app/_services/toast.service'
import { TranslocoModule } from '@jsverse/transloco'

@Component({
  selector: 'toast',
  standalone: true,
  imports: [CommonModule, IconComponent, TranslocoModule],
  templateUrl: './toast.component.html',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class ToastComponent implements OnInit, OnDestroy {
  toast: Toast | null = null
  ToastType = ToastType

  private dismissTimeout?: ReturnType<typeof setTimeout>
  private progressInterval?: ReturnType<typeof setInterval>
  private toastStateSubscription?: Subscription

  constructor(
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  getToastInterpolationParams(
    activeToast: Toast | null
  ): Record<string, string | null | undefined> {
    if (!activeToast) {
      return { entity: null }
    }
    return {
      entity: activeToast.entity,
      ...(activeToast.translationParams ?? {}),
    }
  }

  ngOnInit(): void {
    this.toastStateSubscription = this.toastService.toastState$.subscribe(
      (toast: Toast) => {
        this.showToast(toast)
      }
    )
  }

  ngOnDestroy(): void {
    this.toastStateSubscription?.unsubscribe()
    this.clearToastTimers()
  }

  showToast(toast: Toast) {
    this.clearToastTimers()
    this.toast = toast
    this.cdr.markForCheck()

    const interval = 10
    const decrement = 100 / (toast.duration / interval)

    this.progressInterval = setInterval(() => {
      if (toast.progress > 0) {
        toast.progress -= decrement
        this.cdr.markForCheck()
      } else {
        this.removeToast()
      }
    }, interval)

    this.dismissTimeout = setTimeout(() => {
      this.removeToast()
    }, toast.duration)
  }

  removeToast() {
    this.clearToastTimers()
    this.toast = null
    this.cdr.markForCheck()
  }

  private clearToastTimers(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = undefined
    }
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout)
      this.dismissTimeout = undefined
    }
  }
}

export enum ToastType {
  Success = 'SUCCESS',
  Error = 'ERROR',
  Info = 'INFO',
  Warning = 'WARNING',
}

export enum ToastMessage {
  Submit_Success = 'submit_success',
  Submit_Error = 'submit_error',
  Submit_Invalid = 'submit_invalid',
  Edit_Success = 'edit_success',
  Edit_Error = 'edit_error',
  Edit_Invalid = 'edit_invalid',
  Update_Success = 'update_success',
  Update_Error = 'update_error',
  Update_Invalid = 'update_invalid',
  Add_Success = 'add_success',
  Add_Error = 'add_error',
  Add_Error_Duplicate = 'add_error_duplicate',
  Add_Roles_Duplicate_Success = 'add_roles_duplicate_success',
  Add_Roles_Duplicate_Error = 'add_roles_duplicate_error',
  Delete_Success = 'delete_success',
  Delete_Error = 'delete_error',
  SignIn_Success = 'signin_success',
  SignIn_Error = 'signin_error',
  SignIn_Invalid_Verification_Code = 'signin_invalid_verification_code',
  SignOut_Success = 'signout_success',
  SignOut_Error = 'signout_error',
  ClearCache_Success = 'clearcache_success',
  ClearCache_Error = 'clearcache_error',
  MessageSent_Success = 'messagesent_success',
  MessageSent_Error = 'messagesent_error',
  ReceiptSend_Success = 'receipt_send_success',
  ReceiptSend_Error = 'receipt_send_error',
  OpenLockerWithMessage_Success = 'openlockerwithmessage_success',
  OpenLockerWithMessage_Error = 'openlockerwithmessage_error',
  OpenLocker_Success = 'openlocker_success',
  OpenLocker_Error = 'openlocker_error',
  OpenLocker_ColumnOffline_Error = 'openlocker_column_offline_error',
  OpenAndTerminateWithMessage_Success = 'openandterminatewithmessage_success',
  OpenAndTerminateWithMessage_Error = 'openandterminatewithmessage_error',
  OpenAndTerminate_Success = 'openandterminate_success',
  OpenAndTerminate_Error = 'openandterminate_error',
  VerificationCode_Success = 'verificationcode_success',
  VerificationCode_Error = 'verificationcode_error',
  TerminateWithoutOpeningWithMessage_Success = 'terminatewithoutopeningwithmessage_success',
  TerminateWithoutOpeningWithMessage_Error = 'terminatewithoutopeningwithmessage_error',
  TerminateWithoutOpening_Success = 'terminatewithoutopening_success',
  TerminateWithoutOpening_Error = 'terminatewithoutopening_error',
  TerminateRefund_Success = 'terminaterefund_success',
  TerminateRefund_Error = 'terminaterefund_error',
  TerminateRefundWithMessage_Success = 'terminaterefundwithmessage_success',
  TerminateRefundWithMessage_Error = 'terminaterefundwithmessage_error',
  Unauthorized_Error = 'unauthorized_error',
  Unspecified_Error = 'unspecified_error',
  Set_Error = 'set_error',
  ApiLevelNoLongerSupported_Error = 'apilevelnolongersupported_error',
  Throttling_Error = 'throttling_error',
  Duplicate_Error = 'duplicate_error',
  WrongPassword_Error = 'wrongpassword_error',
  NotFound_Error = 'notfound_error',
  Forbidden_Error = 'forbidden_error',
  IllegalArgument_Error = 'illegalargument_error',
  IllegalState_Error = 'illegalstate_error',
  Ineligible_Error = 'ineligible_error',
  NoData_Error = 'nodata_error',
  NoExtra_Error = 'noextra_error',
  Depleted_Error = 'depleted_error',
  PreconditionFailed_Error = 'preconditionfailed_error',
  FileError_Error = 'fileerror_error',
  Expired_Error = 'expired_error',
  Default_Currency_Auto_Adjusted_Info = 'default_currency_auto_adjusted_info',
  Invalid_Phone_Save_Error = 'invalid_phone_save_error',
  Invalid_Email_Save_Error = 'invalid_email_save_error',
  Connection_Restored = 'connection_restored',
  Details_Copied = 'details_copied',
  Network_Check_Snoozed_Until = 'network_check_snoozed_until',
  ClearFirmwareUpdate_Success = 'clear_firmware_update_success',
  ClearFirmwareUpdate_Error = 'clear_firmware_update_error',
  ClearFirmwareUpdate_Partial = 'clear_firmware_update_partial',
}
